import QRCode from 'qrcode';
import { io } from 'socket.io-client';
import { TimeSignalScheduler, type TimeSignalPhase } from './time-signal';
import type {
  FeaturedEntry,
  IndicatorSnapshot,
  LivestreamEntry,
  NewsItem,
  PolymarketMarket,
  RadioNowPlaying,
  RoomSnapshot,
  VolumeOverride,
  WeatherForecastPoint
} from '../../server/types';

interface OtpPayload {
  code: string;
  expiresAt: number;
  validForMs: number;
}

interface WeatherDescriptor {
  label: string;
  emoji: string;
}

const KIOSK_IDENTITY = "AI — 'ay-ey'— Asistencia al Investigador por Espacio Público";

const params = new URLSearchParams(window.location.search);
let roomId: string | null = params.get('room');
let displayToken = params.get('displayToken') ?? '';
const controlUrlParam = params.get('controlUrl');
const defaultRoomId = 'oficina';
const defaultRoomLabel = 'Oficina EP';
const roomNameMap: Record<string, string> = {
  [defaultRoomId]: defaultRoomLabel
};
const roomScheduleMap: Record<string, { start: string; end: string }> = {};
const VERSION_POLL_MS = 60_000;
const CHILE_TZ = 'America/Santiago';
const COOKIE_NAME = 'kiosk_session';
const COOKIE_MAX_AGE = 60 * 60 * 24 * 30; // 30 días
const FALLBACK_ACTIVE_HOURS = { start: '08:00', end: '19:00' };
const FEATURE_ROTATION_MS = 60_000;
const LIVESTREAM_ROTATION_MS = 90_000;
const LIVESTREAM_HEALTH_CACHE_MS = 120_000;
const LIVESTREAM_VALIDATION_TIMEOUT_MS = 10_000;
const FRESH_NEWS_THRESHOLD_MIN = 45;
const NEWS_VISIBLE_COUNT = 6;
const NEWS_PINNED_COUNT = 4;
const NEWS_ROTATION_DUPLICATION = 2;
const NEWS_MAX_ITEMS = 18;
const CENTRAL_ROTATION_MS = 30_000;
const SIDE_ROTATION_MS = 24_000;
const OTP_REFRESH_INTERVAL_MS = 45_000;
const INDICATOR_DENYLIST = ['dólar acuerdo', 'dolar acuerdo'];
const FORECAST_TARGET_HOURS = [9, 13, 19];
roomScheduleMap[defaultRoomId] = { ...FALLBACK_ACTIVE_HOURS };

const NEWS_BRANDS: Record<string, { label: string; logo: string }> = {
  'theclinic.cl': {
    label: 'The Clinic',
    logo: 'https://upload.wikimedia.org/wikipedia/commons/4/48/The_Clinic_Logo.png'
  },
  'df.cl': {
    label: 'Diario Financiero',
    logo: 'https://r.prcdn.co/res/es-ar/g25056/t490995764/2/images/new-logo-diariofinanciero.png'
  },
  'latercera.com': {
    label: 'La Tercera',
    logo: 'https://upload.wikimedia.org/wikipedia/commons/3/38/Logolatercera2017.png'
  },
  't13.cl': {
    label: 'T13 Radio',
    logo: 'https://static.mytuner.mobi/media/tvos_radios/344/tele-trece-radio.4fb17cdd.jpg'
  }
};

type RoomOption = {
  id: string;
  displayName: string;
  timezone?: string;
  activeHours?: { start: string; end: string };
};

type SessionCookie = {
  roomId: string;
  displayToken: string;
  roomName?: string;
  activeHours?: { start: string; end: string };
};

type TickerItem = {
  label: string;
  value: string;
  change?: string;
  direction?: 'up' | 'down' | 'flat';
};

type RadioDisplayState = {
  streamUrl: string | null;
  streamLabel: string;
  statusLabel: string;
  isPlaying: boolean;
  ducking: boolean;
  nowPlaying: string;
  volumePercent: number;
  overrides: VolumeOverride[];
};

type WeatherDisplayState = {
  summary: string;
  descriptor: WeatherDescriptor;
  temperature: string;
  wind?: string | null;
  humidity?: string | null;
  slots: Array<{ icon: string; time: string; temp: string; meta: string; targetHour: number }>;
};

type ServerClockPayload = {
  epochMs: number;
  iso?: string;
  timezone?: string;
};

let availableRooms: RoomOption[] = [];

let controlUrl = '';
let appStarted = false;
let overlayMode: 'join' | 'create' = 'join';
let lastSnapshot: RoomSnapshot | null = null;
let lastOtp: OtpPayload | null = null;
const OTP_STORAGE_KEY = 'kiosk:lastOtp';
let otpTimer: number | undefined;
let currentRadioStream: string | null = null;
let currentBuildId: string | null = null;
let lastOtpRequestAt = 0;
let statusMessageTimer: number | undefined;
let previousParticipantIds = new Set<string>();

let newsItems: NewsItem[] = [];
let newsIndex = 0;
let newsTimer: number | undefined;

export type Slide = { id: string; title: string; html: string };

let centralSlides: Slide[] = [];
let centralIndex = 0;
let centralTimer: number | undefined;

let sideSlides: Slide[] = [];
let sideIndex = 0;
let sideTimer: number | undefined;
let panelListenersBound = false;

let otpRefreshTimer: number | undefined;
let otpRefreshTimeout: number | undefined;
let otpManualVisible = false;
let isLocked = false;
let lastWeatherState: WeatherDisplayState | null = null;
let lastRadioState: RadioDisplayState | null = null;
let timeSignal: TimeSignalScheduler | null = null;

type LivestreamHealthStatus = 'pending' | 'ok' | 'fail';
const livestreamHealth = new Map<string, { status: LivestreamHealthStatus; checkedAt: number }>();

const els = {
  clock: document.querySelector<HTMLElement>('#clock'),
  clockDate: document.querySelector<HTMLElement>('#clockDate'),
  activeHours: document.querySelector<HTMLElement>('#activeHours'),
  statusMessage: document.querySelector<HTMLElement>('#statusMessage'),
  weatherSummary: document.querySelector<HTMLElement>('#weatherSummary'),
  radioSummary: document.querySelector<HTMLElement>('#radioSummary'),
  connectionState: document.querySelector<HTMLElement>('#connectionState'),
  serviceState: document.querySelector<HTMLElement>('#serviceState'),
  timeSignalOverlay: document.querySelector<HTMLDivElement>('#timeSignalOverlay'),
  timeSignalMessage: document.querySelector<HTMLElement>('#timeSignalMessage'),
  timeSignalCountdown: document.querySelector<HTMLElement>('#timeSignalCountdown'),
  timeSignalSubtitle: document.querySelector<HTMLElement>('#timeSignalSubtitle'),
  otpCode: document.querySelector<HTMLElement>('#otpCode'),
  otpExpiry: document.querySelector<HTMLElement>('#otpExpiry'),
  qrCanvas: document.querySelector<HTMLCanvasElement>('#qrCanvas'),
  qrInlineCanvas: document.querySelector<HTMLCanvasElement>('#qrInline'),
  qrInlineBlock: document.querySelector<HTMLDivElement>('#qrInlineBlock'),
  qrNavbarCanvas: document.querySelector<HTMLCanvasElement>('#qrNav'),
  qrNavbarBlock: document.querySelector<HTMLDivElement>('#navbarQr'),
  otpOverlay: document.querySelector<HTMLDivElement>('#otpOverlay'),
  otpTrigger: document.querySelector<HTMLButtonElement>('#otpTrigger'),
  qrTrigger: document.querySelector<HTMLButtonElement>('#qrTrigger'),
  otpClose: document.querySelector<HTMLButtonElement>('#otpClose'),
  lockOverlay: document.querySelector<HTMLDivElement>('#lockOverlay'),
  lockOverlayClose: document.querySelector<HTMLButtonElement>('#lockOverlayClose'),
  radioState: document.querySelector<HTMLElement>('#radioState'),
  radioTrack: document.querySelector<HTMLElement>('#radioTrack'),
  radioAudio: document.querySelector<HTMLAudioElement>('#radioPlayer'),
  radioPlayToggle: document.querySelector<HTMLButtonElement>('#radioPlayToggle'),
  radioVolumeUp: document.querySelector<HTMLButtonElement>('#radioVolumeUp'),
  radioVolumeDown: document.querySelector<HTMLButtonElement>('#radioVolumeDown'),
  radioVolumeValue: document.querySelector<HTMLElement>('#radioVolumeValue'),
  volumeBar: document.querySelector<HTMLProgressElement>('#volumeProgress'),
  volumeLabel: document.querySelector<HTMLElement>('#volumeLabel'),
  volumeOverrides: document.querySelector<HTMLElement>('#volumeOverrides'),
  youtubeQueue: document.querySelector<HTMLElement>('#youtubeQueue'),
  overlayBoard: document.querySelector<HTMLElement>('#overlayBoard'),
  notesBoard: document.querySelector<HTMLElement>('#notesBoard'),
  indicators: document.querySelector<HTMLElement>('#indicators'),
  pollStage: document.querySelector<HTMLElement>('#pollStage'),
  weather: document.querySelector<HTMLElement>('#weather'),
  news: document.querySelector<HTMLElement>('#newsList'),
  participants: document.querySelector<HTMLElement>('#participants'),
  mainPanel: document.querySelector<HTMLElement>('#mainPanel'),
  sidePanel: document.querySelector<HTMLElement>('#sidePanel'),
  mediaNow: document.querySelector<HTMLElement>('#mediaNow'),
  stateMeta: document.querySelector<HTMLElement>('#stateMeta'),
  storyRotator: document.querySelector<HTMLElement>('#storyRotator'),
  stageContainer: document.querySelector<HTMLElement>('.layout__stage'),
  livestreamFrame: document.querySelector<HTMLIFrameElement>('#livestreamFrame'),
  livestreamTitle: document.querySelector<HTMLElement>('#livestreamTitle'),
  livestreamMeta: document.querySelector<HTMLElement>('#livestreamMeta'),
  polymarketList: document.querySelector<HTMLElement>('#polymarketList'),
  polymarketSummary: document.querySelector<HTMLElement>('#polymarketSummary'),
  polymarketChart: document.querySelector<HTMLElement>('#polymarketChart'),
  ticker: document.querySelector<HTMLElement>('#ticker'),
  tickerTrack: document.querySelector<HTMLElement>('#tickerTrack'),
  themeToggle: document.querySelector<HTMLButtonElement>('#themeToggle'),
  roomOverlay: document.querySelector<HTMLDivElement>('#roomOverlay'),
  roomTabJoin: document.querySelector<HTMLButtonElement>('#roomTabJoin'),
  roomTabCreate: document.querySelector<HTMLButtonElement>('#roomTabCreate'),
  roomJoinSection: document.querySelector<HTMLDivElement>('#roomJoinSection'),
  roomCreateSection: document.querySelector<HTMLDivElement>('#roomCreateSection'),
  roomJoinBtn: document.querySelector<HTMLButtonElement>('#roomJoin'),
  roomCancelBtn: document.querySelector<HTMLButtonElement>('#roomCancel'),
  roomCreateBtn: document.querySelector<HTMLButtonElement>('#roomCreate'),
  roomCancelCreateBtn: document.querySelector<HTMLButtonElement>('#roomCancelCreate'),
  roomSelect: document.querySelector<HTMLSelectElement>('#roomSelect'),
  roomKeyInput: document.querySelector<HTMLInputElement>('#roomKey'),
  roomCreateNameInput: document.querySelector<HTMLInputElement>('#roomCreateName'),
  roomCreateStartInput: document.querySelector<HTMLInputElement>('#roomCreateStart'),
  roomCreateEndInput: document.querySelector<HTMLInputElement>('#roomCreateEnd'),
  lockDisplayBtn: document.querySelector<HTMLButtonElement>('#lockDisplay'),
  logoutDisplayBtn: document.querySelector<HTMLButtonElement>('#logoutDisplay')
};

const socket = io({
  transports: ['websocket'],
  autoConnect: false
});

if (!displayToken) {
  console.warn('displayToken ausente. Añade ?displayToken=TOKEN en la URL.');
}

void boot();

async function boot(): Promise<void> {
  await loadRooms();
  restoreSessionFromCookie();
  hydrateOtpFromStorage();
  setupOverlayListeners();
  setupDisplayControls();
  init();
}

function init(): void {
  if (!roomId) {
    roomId = availableRooms[0]?.id ?? defaultRoomId;
  }
  populateRoomSelect();
  if (!displayToken) {
    showRoomOverlay();
    return;
  }
  startApp(roomId);
}

function startApp(room: string): void {
  if (appStarted) return;
  roomId = room;
  controlUrl = resolveControlUrl(room);
  hideRoomOverlay();
  refreshQrCanvases();
  showOtpOverlay('auto');
  bindPanelRotationListeners();
  if (room && displayToken) {
    persistSession(room, displayToken, roomNameMap[room], roomScheduleMap[room]);
  }
  startClock();
  setupTimeSignal();
  if (timeSignal) {
    timeSignal.setIdentity(KIOSK_IDENTITY);
  }
  updateConnectionState(false);
  setupSocket();
  startVersionWatcher();
  appStarted = true;
}

async function loadRooms(): Promise<void> {
  try {
    const response = await fetch('/api/rooms', { cache: 'no-store' });
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }
    const rooms = await response.json();
    if (Array.isArray(rooms)) {
      availableRooms = rooms
        .filter((room): room is RoomOption => room && typeof room.id === 'string')
        .map((room) => ({
          id: room.id,
          displayName: typeof room.displayName === 'string' ? room.displayName : room.id,
          timezone: typeof room.timezone === 'string' ? room.timezone : undefined,
          activeHours: room.activeHours
        }));
    }
  } catch (error) {
    console.warn('[kiosk] no se pudo obtener listado de pantallas', error);
  }
  if (!availableRooms.length) {
    availableRooms = [{ id: defaultRoomId, displayName: defaultRoomLabel }];
  }
  for (const room of availableRooms) {
    if (!roomNameMap[room.id]) {
      roomNameMap[room.id] = room.displayName ?? room.id;
    }
  }
}

async function fetchServerClock(): Promise<ServerClockPayload> {
  const response = await fetch('/api/time', { cache: 'no-store' });
  if (!response.ok) {
    throw new Error(`HTTP ${response.status}`);
  }
  const payload = (await response.json()) as ServerClockPayload;
  if (typeof payload.epochMs !== 'number') {
    throw new Error('Respuesta inválida de /api/time');
  }
  return payload;
}

function populateRoomSelect(): void {
  if (!els.roomSelect) return;
  const uniqueRooms = new Map<string, RoomOption>();
  for (const room of availableRooms) {
    uniqueRooms.set(room.id, room);
  }
  if (roomId && !uniqueRooms.has(roomId)) {
    uniqueRooms.set(roomId, {
      id: roomId,
      displayName: roomNameMap[roomId] ?? roomId
    });
  }
  const options = Array.from(uniqueRooms.values());
  els.roomSelect.innerHTML = options
    .map((room) => {
      const selected = room.id === roomId ? ' selected' : '';
      const label = roomNameMap[room.id] ?? room.displayName ?? room.id;
      return `<option value="${escapeHtml(room.id)}"${selected}>${escapeHtml(label)}</option>`;
    })
    .join('');
}

function showRoomOverlay(): void {
  populateRoomSelect();
  setOverlayMode('create');
  const scheduleOverride = roomScheduleMap[defaultRoomId] ?? lastSnapshot?.schedule.activeHours ?? FALLBACK_ACTIVE_HOURS;
  if (els.roomCreateNameInput) {
    els.roomCreateNameInput.value = roomNameMap[defaultRoomId] ?? defaultRoomLabel;
    els.roomCreateNameInput.classList.remove('is-error');
  }
  if (els.roomCreateStartInput) {
    els.roomCreateStartInput.value = scheduleOverride.start;
    els.roomCreateStartInput.classList.remove('is-error');
  }
  if (els.roomCreateEndInput) {
    els.roomCreateEndInput.value = scheduleOverride.end;
    els.roomCreateEndInput.classList.remove('is-error');
  }
  if (els.roomKeyInput) {
    els.roomKeyInput.value = '';
    els.roomKeyInput.classList.remove('is-error');
  }
  if (els.roomSelect && roomId) {
    els.roomSelect.value = roomId;
  }
  els.roomOverlay?.classList.add('visible');
  window.setTimeout(() => {
    if (overlayMode === 'create') {
      els.roomCreateNameInput?.focus();
    } else {
      els.roomKeyInput?.focus();
    }
  }, 0);
}

function hideRoomOverlay(): void {
  els.roomOverlay?.classList.remove('visible');
}

function handleRoomJoin(): void {
  const selectedRoom = els.roomSelect?.value?.trim() || defaultRoomId;
  const roomKey = els.roomKeyInput?.value.trim() ?? '';
  if (!roomKey) {
    els.roomKeyInput?.classList.add('is-error');
    els.roomKeyInput?.focus();
    return;
  }
  els.roomKeyInput?.classList.remove('is-error');
  displayToken = roomKey;
  const schedule = roomScheduleMap[selectedRoom];
  persistSession(selectedRoom, roomKey, roomNameMap[selectedRoom], schedule);
  applyRoom(selectedRoom, roomKey);
}

function handleRoomCreate(): void {
  const nameRaw = els.roomCreateNameInput?.value?.trim() ?? '';
  const name = nameRaw || defaultRoomLabel;
  const startRaw = els.roomCreateStartInput?.value?.trim() ?? '';
  const endRaw = els.roomCreateEndInput?.value?.trim() ?? '';
  const start = startRaw || FALLBACK_ACTIVE_HOURS.start;
  const end = endRaw || FALLBACK_ACTIVE_HOURS.end;
  roomNameMap[defaultRoomId] = name;
  roomScheduleMap[defaultRoomId] = { start, end };
  persistSession(defaultRoomId, displayToken, name, roomScheduleMap[defaultRoomId]);
  if (lastSnapshot) {
    renderMeta(lastSnapshot);
  }
  hideRoomOverlay();
  if (!appStarted) {
    startApp(defaultRoomId);
  } else {
    requestOtpRefresh('create-final');
  }
  announceStatus(`Pantalla lista: ${name}`);
}

function applyRoom(newRoom: string, token: string): void {
  const schedule = roomScheduleMap[newRoom];
  persistSession(newRoom, token, roomNameMap[newRoom], schedule);
  const newParams = new URLSearchParams();
  newParams.set('room', newRoom);
  newParams.set('displayToken', token);
  if (controlUrlParam) {
    newParams.set('controlUrl', controlUrlParam);
  }
  const queryString = newParams.toString();
  window.location.replace(`${window.location.pathname}?${queryString}`);
}

function setupOverlayListeners(): void {
  els.roomTabJoin?.addEventListener('click', () => setOverlayMode('join'));
  els.roomTabCreate?.addEventListener('click', () => setOverlayMode('create'));

  els.roomJoinBtn?.addEventListener('click', handleRoomJoin);
  els.roomCreateBtn?.addEventListener('click', handleRoomCreate);
  els.roomCancelBtn?.addEventListener('click', hideRoomOverlay);
  els.roomCancelCreateBtn?.addEventListener('click', hideRoomOverlay);

  els.roomKeyInput?.addEventListener('keydown', (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      handleRoomJoin();
    }
  });

  els.roomSelect?.addEventListener('change', (event) => {
    const target = event.target as HTMLSelectElement | null;
    if (target?.value) {
      roomId = target.value;
    }
  });
}

function setupDisplayControls(): void {
  els.radioPlayToggle?.addEventListener('click', () => {
    if (!lastSnapshot || !socket.connected) return;
    const target = !lastSnapshot.radio.isPlaying;
    socket.emit('display:radio_toggle', { isPlaying: target });
  });
  const bump = (delta: number): void => {
    if (!socket.connected) return;
    socket.emit('display:volume_bump', { delta });
  };
  els.radioVolumeUp?.addEventListener('click', () => bump(6));
  els.radioVolumeDown?.addEventListener('click', () => bump(-6));

  els.otpTrigger?.addEventListener('click', () => {
    showOtpOverlay('manual');
    requestOtpRefresh('manual');
  });
  const showQr = (): void => {
    showOtpOverlay('manual');
    if (!lastOtp) {
      requestOtpRefresh('manual');
    }
  };
  els.qrTrigger?.addEventListener('click', showQr);
  els.otpClose?.addEventListener('click', () => {
    otpManualVisible = false;
    hideOtpOverlay();
  });

  els.lockDisplayBtn?.addEventListener('click', () => toggleLock());
  els.lockOverlayClose?.addEventListener('click', () => toggleLock(false));
  els.logoutDisplayBtn?.addEventListener('click', () => {
    clearDisplaySession();
  });
}

function setupTimeSignal(): void {
  // No activar en la vista principal; solo la página de testing lo inicia.
}

function handleTimeSignalPhase(phase: TimeSignalPhase): void {
  if (phase === 'prelude') {
    announceStatus('Señal horaria en 7 segundos (Choa)');
  } else if (phase === 'active') {
    announceStatus('¡Señal horaria emitida!');
  }
}

function bindPanelRotationListeners(): void {
  if (panelListenersBound) return;
  document.addEventListener('visibilitychange', () => {
    if (document.hidden) {
      stopCentralRotation();
      stopSideRotation();
    } else {
      if (centralSlides.length) {
        showCentralSlide();
      }
      if (sideSlides.length) {
        showSideSlide();
      }
      if (centralSlides.length > 1) {
        startCentralRotation();
      }
      if (sideSlides.length > 1) {
        startSideRotation();
      }
    }
  });
  panelListenersBound = true;
}

function updateCentralSlides(snapshot: RoomSnapshot): void {
  const nextSlides = buildCentralSlides(snapshot);
  const slides = nextSlides.length
    ? nextSlides
    : [{ id: 'central-empty', title: '', html: wrapFocusPanel('<p class="empty">Sin información disponible</p>', 'focus-panel--empty') }];
  const changed = slidesChanged(centralSlides, slides);
  centralSlides = slides;
  if (centralIndex >= centralSlides.length) {
    centralIndex = 0;
  }
  showCentralSlide();
  if (changed || (!centralTimer && centralSlides.length > 1 && !document.hidden)) {
    startCentralRotation();
  }
  if (centralSlides.length <= 1) {
    stopCentralRotation();
  }
}

function updateSideSlides(snapshot: RoomSnapshot): void {
  const nextSlides = buildSideSlides(snapshot);
  const slides = nextSlides.length
    ? nextSlides
    : [{ id: 'side-empty', title: '', html: wrapFocusPanel('<p class="empty">Sin datos disponibles</p>', 'focus-panel--empty') }];
  const changed = slidesChanged(sideSlides, slides);
  sideSlides = slides;
  if (sideIndex >= sideSlides.length) {
    sideIndex = 0;
  }
  showSideSlide();
  if (changed || (!sideTimer && sideSlides.length > 1 && !document.hidden)) {
    startSideRotation();
  }
  if (sideSlides.length <= 1) {
    stopSideRotation();
  }
}

function showCentralSlide(): void {
  const slide = centralSlides[centralIndex] ?? centralSlides[0];
  if (!slide || !els.mainPanel) return;
  els.mainPanel.innerHTML = slide.html;
  const section = els.mainPanel.closest<HTMLElement>('.panel');
  if (section) {
    if (slide.title) {
      section.setAttribute('aria-label', slide.title);
    } else {
      section.removeAttribute('aria-label');
    }
  }
}

function showSideSlide(): void {
  const slide = sideSlides[sideIndex] ?? sideSlides[0];
  if (!slide || !els.sidePanel) return;
  els.sidePanel.innerHTML = slide.html;
  const aside = els.sidePanel.closest<HTMLElement>('.panel');
  if (aside) {
    if (slide.title) {
      aside.setAttribute('aria-label', slide.title);
    } else {
      aside.removeAttribute('aria-label');
    }
  }
}

function startCentralRotation(): void {
  stopCentralRotation();
  if (centralSlides.length <= 1 || document.hidden) return;
  centralTimer = window.setInterval(() => {
    if (!centralSlides.length) return;
    centralIndex = (centralIndex + 1) % centralSlides.length;
    showCentralSlide();
  }, CENTRAL_ROTATION_MS);
}

function stopCentralRotation(): void {
  if (centralTimer) {
    window.clearInterval(centralTimer);
    centralTimer = undefined;
  }
}

function startSideRotation(): void {
  stopSideRotation();
  if (sideSlides.length <= 1 || document.hidden) return;
  sideTimer = window.setInterval(() => {
    if (!sideSlides.length) return;
    sideIndex = (sideIndex + 1) % sideSlides.length;
    showSideSlide();
  }, SIDE_ROTATION_MS);
}

function stopSideRotation(): void {
  if (sideTimer) {
    window.clearInterval(sideTimer);
    sideTimer = undefined;
  }
}

function slidesChanged(prev: Slide[], next: Slide[]): boolean {
  if (prev.length !== next.length) return true;
  for (let i = 0; i < next.length; i++) {
    if (prev[i]?.id !== next[i]?.id) {
      return true;
    }
  }
  return false;
}

function clearDisplaySession(): void {
  displayToken = '';
  appStarted = false;
  hideOtpOverlay();
  if (isLocked) {
    toggleLock(false);
  }
  stopOtpAutoRefresh();
  document.cookie = `${COOKIE_NAME}=; max-age=0; path=/; SameSite=Lax`;
  window.setTimeout(() => {
    window.location.replace(window.location.pathname);
  }, 150);
}

function persistSession(
  room: string,
  token: string,
  roomName?: string,
  activeHours?: { start: string; end: string }
): void {
  const normalizedRoom = room.trim();
  if (!normalizedRoom) return;
  const normalizedToken = token.trim();
  const normalizedName = typeof roomName === 'string' ? roomName.trim() : undefined;
  let schedule: SessionCookie['activeHours'];
  if (activeHours && typeof activeHours.start === 'string' && typeof activeHours.end === 'string') {
    schedule = { start: activeHours.start, end: activeHours.end };
    roomScheduleMap[normalizedRoom] = schedule;
  }
  if (normalizedName) {
    roomNameMap[normalizedRoom] = normalizedName;
  }
  try {
    const payload: SessionCookie = {
      roomId: normalizedRoom,
      displayToken: normalizedToken
    };
    if (normalizedName) {
      payload.roomName = normalizedName;
    }
    if (schedule) {
      payload.activeHours = schedule;
    }
    const encoded = encodeURIComponent(JSON.stringify(payload));
    document.cookie = `${COOKIE_NAME}=${encoded}; max-age=${COOKIE_MAX_AGE}; path=/; SameSite=Lax`;
  } catch (error) {
    console.warn('[kiosk] no se pudo guardar la sesión', error);
  }
}

function restoreSessionFromCookie(): void {
  const session = readSessionCookie();
  if (!session) return;

  if (session.roomName) {
    roomNameMap[session.roomId] = session.roomName;
  }
  if (session.activeHours) {
    roomScheduleMap[session.roomId] = session.activeHours;
  }
  if (!roomId && session.roomId) {
    roomId = session.roomId;
  }
  if (!displayToken && session.displayToken) {
    displayToken = session.displayToken;
  }
  const search = new URLSearchParams(window.location.search);
  let updated = false;
  if (session.roomId && !search.has('room')) {
    search.set('room', session.roomId);
    updated = true;
  }
  if (session.displayToken && !search.has('displayToken')) {
    search.set('displayToken', session.displayToken);
    updated = true;
  }
  if (updated) {
    const nextQuery = search.toString();
    const nextUrl = nextQuery ? `${window.location.pathname}?${nextQuery}` : window.location.pathname;
    window.history.replaceState(null, document.title, nextUrl);
  }
}

function readSessionCookie(): SessionCookie | null {
  const raw = getCookieValue(COOKIE_NAME);
  if (!raw) return null;
  try {
    const decoded = decodeURIComponent(raw);
    const data = JSON.parse(decoded) as Partial<SessionCookie> & {
      activeHours?: { start?: unknown; end?: unknown };
    };
    const room = typeof data.roomId === 'string' ? data.roomId.trim() : '';
    if (!room) return null;
    const token = typeof data.displayToken === 'string' ? data.displayToken.trim() : '';
    const session: SessionCookie = {
      roomId: room,
      displayToken: token
    };
    if (typeof data.roomName === 'string' && data.roomName.trim()) {
      session.roomName = data.roomName.trim();
    }
    const hours = data.activeHours;
    if (
      hours &&
      typeof hours === 'object' &&
      typeof hours.start === 'string' &&
      typeof hours.end === 'string'
    ) {
      session.activeHours = { start: hours.start, end: hours.end };
    }
    return session;
  } catch (error) {
    console.warn('[kiosk] sesión inválida en cookie', error);
    return null;
  }
}

function getCookieValue(name: string): string | null {
  const prefix = `${name}=`;
  const parts = document.cookie.split(';');
  for (const part of parts) {
    const trimmed = part.trim();
    if (trimmed.startsWith(prefix)) {
      return trimmed.slice(prefix.length);
    }
  }
  return null;
}

function showOtpOverlay(source: 'manual' | 'auto'): void {
  if (!els.otpOverlay) return;
  if (source === 'manual') {
    otpManualVisible = true;
  }
  els.otpOverlay.classList.add('visible');
  refreshQrCanvases(true);
  const needsRefresh = !lastOtp || Date.now() >= lastOtp.expiresAt - 5_000;
  if (needsRefresh) {
    requestOtpRefresh(source === 'manual' ? 'manual-open' : 'auto-open');
  }
}

function hideOtpOverlay(): void {
  if (!els.otpOverlay) return;
  els.otpOverlay.classList.remove('visible');
  otpManualVisible = false;
}

function toggleLock(force?: boolean): void {
  const next = typeof force === 'boolean' ? force : !isLocked;
  isLocked = next;
  if (isLocked) {
    els.lockOverlay?.classList.add('visible');
    if (els.lockDisplayBtn) {
      els.lockDisplayBtn.textContent = '🔓';
      els.lockDisplayBtn.setAttribute('aria-label', 'Desbloquear pantalla');
    }
    announceStatus('Pantalla bloqueada');
  } else {
    els.lockOverlay?.classList.remove('visible');
    if (els.lockDisplayBtn) {
      els.lockDisplayBtn.textContent = '🔒';
      els.lockDisplayBtn.setAttribute('aria-label', 'Bloquear pantalla');
    }
    announceStatus('Pantalla desbloqueada');
  }
}

function resolveControlUrl(room: string): string {
  const url = controlUrlParam
    ? new URL(controlUrlParam, window.location.origin)
    : new URL('/control.html', window.location.origin);
  url.searchParams.set('room', room);
  if (displayToken) {
    url.searchParams.set('displayToken', displayToken);
  } else {
    url.searchParams.delete('displayToken');
  }
  return url.toString();
}

function setupSocket(): void {
  socket.on('connect', () => {
    const targetRoom = roomId ?? defaultRoomId;
    console.info('[kiosk] conectado');
    updateConnectionState(true);
    socket.emit('display:register', { roomId: targetRoom, displayToken });
    requestOtpRefresh('connect');
    startOtpAutoRefresh();
    announceStatus('Conectado al servidor');
  });

  socket.on('disconnect', () => {
    console.warn('[kiosk] desconectado. Reintentando…');
    updateConnectionState(false);
    stopOtpAutoRefresh();
    announceStatus('Conexión perdida, reintentando…');
  });

  socket.on('room:snapshot', (snapshot: RoomSnapshot) => {
    lastSnapshot = snapshot;
    renderSnapshot(snapshot);
  });

  socket.on('otp:update', (payload: OtpPayload) => {
    lastOtp = payload;
    renderOtp(payload);
  });

  socket.connect();
}

function setOverlayMode(mode: 'join' | 'create'): void {
  overlayMode = mode;
  const joinActive = mode === 'join';
  els.roomJoinSection?.toggleAttribute('hidden', !joinActive);
  els.roomCreateSection?.toggleAttribute('hidden', joinActive);
  if (joinActive) {
    els.roomTabJoin?.classList.add('is-active');
    els.roomTabJoin?.setAttribute('aria-selected', 'true');
    els.roomTabCreate?.classList.remove('is-active');
    els.roomTabCreate?.setAttribute('aria-selected', 'false');
    els.roomKeyInput?.classList.remove('is-error');
  } else {
    els.roomTabCreate?.classList.add('is-active');
    els.roomTabCreate?.setAttribute('aria-selected', 'true');
    els.roomTabJoin?.classList.remove('is-active');
    els.roomTabJoin?.setAttribute('aria-selected', 'false');
    const scheduleOverride = roomScheduleMap[defaultRoomId] ?? lastSnapshot?.schedule.activeHours ?? FALLBACK_ACTIVE_HOURS;
    if (els.roomCreateNameInput) {
      els.roomCreateNameInput.value = roomNameMap[defaultRoomId] ?? defaultRoomLabel;
      els.roomCreateNameInput.classList.remove('is-error');
    }
    if (els.roomCreateStartInput) {
      els.roomCreateStartInput.value = scheduleOverride.start;
      els.roomCreateStartInput.classList.remove('is-error');
    }
    if (els.roomCreateEndInput) {
      els.roomCreateEndInput.value = scheduleOverride.end;
      els.roomCreateEndInput.classList.remove('is-error');
    }
    if (displayToken) {
      requestOtpRefresh('create-enter');
    }
  }
}

function requestOtpRefresh(reason: string): void {
  if (!socket.connected || !displayToken) return;
  const now = Date.now();
  if (now - lastOtpRequestAt < 5_000) return;
  lastOtpRequestAt = now;
  socket.emit('display:refresh_otp', { reason });
}

function startOtpAutoRefresh(): void {
  stopOtpAutoRefresh();
  if (!displayToken || !socket.connected) return;
  otpRefreshTimer = window.setInterval(() => {
    requestOtpRefresh('auto-heartbeat');
  }, OTP_REFRESH_INTERVAL_MS);
}

function hydrateOtpFromStorage(): void {
  try {
    const raw = window.localStorage.getItem(OTP_STORAGE_KEY);
    if (!raw) return;
    const stored = JSON.parse(raw) as OtpPayload;
    if (!stored || typeof stored.code !== 'string') return;
    if (stored.expiresAt <= Date.now()) {
      window.localStorage.removeItem(OTP_STORAGE_KEY);
      return;
    }
    lastOtp = stored;
    renderOtpDisplayOnly(stored);
    updateOtpExpiry();
  } catch (error) {
    console.warn('[kiosk] otp cache inválido', error);
  }
}

function renderOtpDisplayOnly(payload: OtpPayload): void {
  if (els.otpCode) {
    els.otpCode.textContent = payload.code;
  }
  refreshQrCanvases();
  if (els.otpExpiry) {
    const remaining = Math.max(0, payload.expiresAt - Date.now());
    const seconds = Math.ceil(remaining / 1000);
    els.otpExpiry.textContent = `Código caduca en ${seconds}s`;
  }
}

function persistOtp(payload: OtpPayload): void {
  lastOtp = payload;
  try {
    window.localStorage.setItem(OTP_STORAGE_KEY, JSON.stringify(payload));
  } catch (error) {
    console.warn('[kiosk] no se pudo guardar OTP', error);
  }
}

function stopOtpAutoRefresh(): void {
  if (otpRefreshTimer) {
    window.clearInterval(otpRefreshTimer);
    otpRefreshTimer = undefined;
  }
  if (otpRefreshTimeout) {
    window.clearTimeout(otpRefreshTimeout);
    otpRefreshTimeout = undefined;
  }
}

function computeOtpRefreshDelay(payload: OtpPayload): number {
  const now = Date.now();
  const margin = 7_000;
  const fallback = 5_000;
  const target = Math.max(now + fallback, payload.expiresAt - margin);
  return Math.max(fallback, target - now);
}

function scheduleOtpExpiryRefresh(payload: OtpPayload): void {
  if (!displayToken) return;
  if (!socket.connected) {
    const delay = Math.max(5_000, payload.expiresAt - Date.now() - 5_000);
    otpRefreshTimeout = window.setTimeout(() => requestOtpRefresh('retry-offline'), delay);
    return;
  }
  if (otpRefreshTimeout) {
    window.clearTimeout(otpRefreshTimeout);
    otpRefreshTimeout = undefined;
  }
  const delay = computeOtpRefreshDelay(payload);
  otpRefreshTimeout = window.setTimeout(() => {
    requestOtpRefresh('auto-expiry');
  }, delay);
}

function renderSnapshot(snapshot: RoomSnapshot): void {
  const activeSnapshot = snapshot.schedule.activeHours;
  if (activeSnapshot && !roomScheduleMap[snapshot.roomId]) {
    roomScheduleMap[snapshot.roomId] = activeSnapshot;
  }
  updateMeta(snapshot);
  updateServiceState(snapshot);
  const weatherState = deriveWeatherState(snapshot);
  lastWeatherState = weatherState;
  const radioState = deriveRadioState(snapshot);
  lastRadioState = radioState;
  updateHeaderStatus(weatherState, radioState);
  updateRadioPlayback(snapshot, radioState);
  renderRadioControls(radioState);
  updateCentralSlides(snapshot);
  updateSideSlides(snapshot);
  renderTicker(snapshot);
  renderParticipants(snapshot);
}



function formatOverride(override: VolumeOverride, now: number): string {
  const by = override.createdBy ?? 'alguien';
  let detail = 'Volumen';
  if (override.kind === 'mute') {
    detail = 'Silencio';
  } else if (override.kind === 'set' && typeof override.level === 'number') {
    detail = `A ${override.level}%`;
  } else if (override.kind === 'boost' && typeof override.delta === 'number') {
    detail = `+${override.delta}%`;
  }
  const untilLabel = formatChileanTime(override.expiresAt);
  const remainingLabel = formatRemaining(override.expiresAt - now);
  const suffix = remainingLabel ? `${by} • hasta ${untilLabel} (${remainingLabel})` : `${by} • hasta ${untilLabel}`;
  return `<div class="badge">${detail} • ${suffix}</div>`;
}




function selectForecastSlots(
  points: WeatherForecastPoint[],
  fallbackCondition?: string
): Array<{ icon: string; time: string; temp: string; meta: string; targetHour: number }> {
  if (!Array.isArray(points) || !points.length) return [];
  const now = Date.now();
  const slots: Array<{ icon: string; time: string; temp: string; meta: string; targetHour: number }> = [];
  for (const targetHour of FORECAST_TARGET_HOURS) {
    const point = findClosestForecast(points, targetHour, now);
    if (!point) continue;
    const timeLabel = point.label ? escapeHtml(point.label) : escapeHtml(formatHourLabel(point.time));
    const tempLabel = typeof point.temperatureC === 'number' ? `${point.temperatureC.toFixed(1)}°C` : '—';
    const rain = typeof point.rainChance === 'number' ? `${Math.round(point.rainChance)}%` : '—%';
    const wind = typeof point.windKmh === 'number' ? `${Math.round(point.windKmh)} km/h` : '— km/h';
    const icon = selectForecastIcon(point, fallbackCondition);
    slots.push({
      icon,
      time: timeLabel,
      temp: tempLabel,
      meta: `Prob. lluvia ${rain}<br>Viento ${wind}`,
      targetHour
    });
  }
  if (new Date(now).getHours() >= 18) {
    const morningIndex = slots.findIndex((slot) => slot.targetHour === 9);
    if (morningIndex > 0) {
      const [morning] = slots.splice(morningIndex, 1);
      slots.push(morning);
    }
  }
  return slots;
}

function findClosestForecast(points: WeatherForecastPoint[], targetHour: number, now: number): WeatherForecastPoint | null {
  let best: WeatherForecastPoint | null = null;
  let bestScore = Number.POSITIVE_INFINITY;
  for (const point of points) {
    const timestamp = typeof point.time === 'number' ? point.time : deriveForecastTimestamp(point.label);
    if (!timestamp) continue;
    const hoursDiff = Math.abs(new Date(timestamp).getHours() - targetHour);
    const timeDiff = Math.max(0, timestamp - now);
    const score = hoursDiff * 10_000_000 + timeDiff;
    if (score < bestScore) {
      best = point;
      bestScore = score;
    }
  }
  return best;
}

function deriveForecastTimestamp(label?: string): number | null {
  if (!label) return null;
  const match = label.match(/(\d{1,2})[:h](\d{2})?/i);
  if (!match) return null;
  const hours = Number(match[1]);
  const minutes = match[2] ? Number(match[2]) : 0;
  const base = new Date();
  base.setMinutes(0, 0, 0);
  base.setHours(hours, minutes, 0, 0);
  return base.getTime();
}

function formatHourLabel(timestamp?: number): string {
  if (!timestamp) return '--:--';
  return new Intl.DateTimeFormat('es-CL', { hour: '2-digit', minute: '2-digit', hour12: false }).format(new Date(timestamp));
}


function mapFeaturedCategory(category: FeaturedEntry['category']): string {
  switch (category) {
    case 'imagen':
      return 'Imagen del día';
    case 'articulo':
      return 'Artículo destacado';
    case 'actualidad':
      return 'Actualidad';
    case 'efemerides':
      return 'Efemérides';
    default:
      return 'Destacado';
  }
}

function buildCentralSlides(snapshot: RoomSnapshot): Slide[] {
  const slides: Slide[] = [];
  const livestreamSlides = buildLivestreamSlides(snapshot);
  slides.push(...livestreamSlides);
  const efemerideSlides = buildEfemerideSlides(snapshot);
  slides.push(...efemerideSlides);
  const pollSlide = buildPollSlide(snapshot);
  if (pollSlide) slides.push(pollSlide);
  const polymarketSlide = buildPolymarketSlide(snapshot);
  if (polymarketSlide) slides.push(polymarketSlide);
  const newsSlides = buildNewsSlides(snapshot);
  slides.push(...newsSlides);
  return slides;
}

function buildSideSlides(snapshot: RoomSnapshot): Slide[] {
  const slides: Slide[] = [];
  const weatherSlide = buildWeatherSlide(snapshot);
  if (weatherSlide) slides.push(weatherSlide);
  const radioSlide = buildRadioSlide(snapshot);
  if (radioSlide) slides.push(radioSlide);
  const queueSlide = buildQueueSlide(snapshot);
  if (queueSlide) slides.push(queueSlide);
  const overlaySlide = buildOverlaySlide(snapshot);
  if (overlaySlide) slides.push(overlaySlide);
  const notesSlide = buildNotesSlide(snapshot);
  if (notesSlide) slides.push(notesSlide);
  const metaSlide = buildMetaSlide(snapshot);
  if (metaSlide) slides.push(metaSlide);
  return slides;
}

function deriveWeatherState(snapshot: RoomSnapshot): WeatherDisplayState | null {
  const weather = snapshot.dashboard.weather;
  if (!weather) return null;
  const descriptor = describeWeather(weather.condition);
  const temperature = typeof weather.temperatureC === 'number' ? `${weather.temperatureC.toFixed(1)}°C` : '—';
  const wind = typeof weather.windKmh === 'number' ? `${Math.round(weather.windKmh)} km/h` : null;
  const humidity = typeof weather.humidity === 'number' ? `${Math.round(weather.humidity)}% HR` : null;
  const slots = selectForecastSlots(Array.isArray(weather.forecast) ? weather.forecast : [], weather.condition);
  const summaryParts = [descriptor.emoji, temperature, descriptor.label];
  if (wind) summaryParts.push(`Viento ${wind}`);
  if (humidity) summaryParts.push(`Humedad ${humidity}`);
  return {
    summary: summaryParts.filter(Boolean).join(' · '),
    descriptor,
    temperature,
    wind,
    humidity,
    slots
  };
}

function deriveRadioState(snapshot: RoomSnapshot): RadioDisplayState {
  const { radio, radioStreams } = snapshot;
  const stream = radioStreams.find((s) => s.id === radio.streamId) ?? radioStreams[0];
  const streamLabel = stream ? stream.name : radio.streamId ?? 'Radio ambiente';
  const statusLabel = radio.isPlaying ? 'En vivo' : 'Pausa';
  const nowPlaying = formatRadioNowPlaying(snapshot.radioNowPlaying, streamLabel);
  return {
    streamUrl: stream?.url ?? null,
    streamLabel,
    statusLabel,
    isPlaying: radio.isPlaying,
    ducking: radio.ducking ?? false,
    nowPlaying,
    volumePercent: snapshot.volume.current,
    overrides: Array.isArray(snapshot.volume.overrides) ? snapshot.volume.overrides : []
  };
}

function updateHeaderStatus(weatherState: WeatherDisplayState | null, radioState: RadioDisplayState | null): void {
  if (els.weatherSummary) {
    els.weatherSummary.textContent = weatherState
      ? `${weatherState.descriptor.emoji} ${weatherState.temperature}`
      : 'Clima —';
  }
  if (els.radioSummary) {
    if (!radioState) {
      els.radioSummary.textContent = 'Radio desconectada';
    } else {
      const prefix = radioState.isPlaying ? 'En vivo' : 'Silencio';
      els.radioSummary.textContent = `${prefix}: ${radioState.nowPlaying}`;
    }
  }
}

function updateConnectionState(isConnected: boolean): void {
  if (!els.connectionState) return;
  const label = isConnected ? '🟢 Conectado' : '🔴 Desconectado';
  els.connectionState.textContent = label;
  els.connectionState.classList.toggle('is-offline', !isConnected);
}

function updateServiceState(snapshot: RoomSnapshot): void {
  if (!els.serviceState) return;
  const schedule = snapshot.schedule?.activeHours ?? FALLBACK_ACTIVE_HOURS;
  const within = snapshot.schedule?.isWithinActiveHours !== false;
  const tzLabel = timeZoneAbbr(snapshot.schedule?.timezone ?? CHILE_TZ);
  const status = within ? '🟢 Servicio activo' : '🌙 Fuera de horario';
  els.serviceState.textContent = `${status} · ${schedule.start} – ${schedule.end} ${tzLabel}`;
  els.serviceState.classList.toggle('is-offline', !within);
}

function updateRadioPlayback(snapshot: RoomSnapshot, radioState: RadioDisplayState | null): void {
  const audio = els.radioAudio;
  if (!audio) return;
  if (!radioState || !radioState.streamUrl) {
    if (!audio.paused) audio.pause();
    return;
  }
  if (radioState.streamUrl !== currentRadioStream) {
    audio.src = radioState.streamUrl;
    currentRadioStream = radioState.streamUrl;
  }
  const targetVolume = Math.min(1, Math.max(0, radioState.volumePercent / 100));
  if (Math.abs(audio.volume - targetVolume) > 0.01) {
    audio.volume = targetVolume;
  }
  if (radioState.isPlaying) {
    if (audio.paused) {
      void audio.play().catch(() => {
        console.warn('[kiosk] autoplay bloqueado, requiere interacción');
      });
    }
  } else if (!audio.paused) {
    audio.pause();
  }
}

function renderRadioControls(radioState: RadioDisplayState | null): void {
  const now = Date.now();
  if (els.radioState) {
    els.radioState.textContent = radioState
      ? `${radioState.isPlaying ? '🔊' : '⏸️'} ${radioState.streamLabel}`
      : 'Radio desconectada';
  }
  if (els.radioTrack) {
    els.radioTrack.textContent = radioState ? radioState.nowPlaying : '';
  }
  if (els.radioVolumeValue) {
    els.radioVolumeValue.textContent = radioState ? `${Math.round(radioState.volumePercent)}%` : '0%';
  }
  if (els.volumeBar) {
    els.volumeBar.value = radioState ? Math.round(radioState.volumePercent) : 0;
  }
  if (els.volumeLabel) {
    els.volumeLabel.textContent = radioState ? (radioState.isPlaying ? 'En reproducción' : 'En pausa') : '';
  }
  if (els.volumeOverrides) {
    if (!radioState || !radioState.overrides.length) {
      els.volumeOverrides.innerHTML = '<span class="muted">Sin ajustes temporales</span>';
    } else {
      els.volumeOverrides.innerHTML = radioState.overrides
        .map((override) => formatOverride(override, now))
        .join('');
    }
  }
}

function wrapFocusPanel(content: string, modifier?: string): string {
  const base = 'focus-panel';
  const classes = modifier ? `${base} ${modifier}` : base;
  return `<article class="${classes}">${content}</article>`;
}

function buildFocusHeader(options: { tag?: string | null; title?: string | null; subtitle?: string | null }): string {
  const segments: string[] = [];
  if (options.tag) {
    segments.push(`<span class="focus-tag">${escapeHtml(options.tag)}</span>`);
  }
  if (options.title) {
    segments.push(`<h3 class="focus-title">${escapeHtml(options.title)}</h3>`);
  }
  if (options.subtitle) {
    segments.push(`<span class="focus-subtitle">${escapeHtml(options.subtitle)}</span>`);
  }
  return segments.length ? `<div class="focus-header">${segments.join('')}</div>` : '';
}

function buildEfemerideSlides(snapshot: RoomSnapshot): Slide[] {
  const entries = (snapshot.dashboard.featured ?? []).filter((entry) => entry.category === 'efemerides');
  if (!entries.length) return [];
  const chunkSize = entries.length > 4 ? 4 : Math.max(1, entries.length);
  const groups = chunkArray(entries, chunkSize);
  const slides = groups.map((group, groupIndex) => {
    const cards = group
      .map((entry, index) => {
        const description = entry.description ? escapeHtml(entry.description) : '';
        const footer = entry.footer ? `<footer>${escapeHtml(entry.footer)}</footer>` : '';
        const highlights = extractEfemerideHighlights(entry.description ?? entry.title ?? '');
        const badges = highlights.length
          ? highlights.map((label) => `<span class="badge badge--efemeride">${escapeHtml(label)}</span>`).join(' ')
          : '<span class="badge badge--efemeride">Efeméride</span>';
        return `
          <div class="efemeride-card">
            <header class="efemeride-card__header">
              ${badges}
              <h3>${escapeHtml(entry.title ?? `Efeméride ${index + 1}`)}</h3>
            </header>
            ${description ? `<p>${description}</p>` : ''}
            ${footer}
          </div>
        `;
      })
      .join('');
    const header = buildFocusHeader({ tag: 'Efemérides', subtitle: formatChileanDate(snapshot.serverTime) });
    const body = `${header}<div class="efemeride-grid">${cards}</div>`;
    return {
      id: `efemeride-${groupIndex}`,
      title: '',
      html: wrapFocusPanel(body, 'focus-panel--efemeride')
    };
  });
  if (slides.length > 1) {
    slides.push(...slides.map((slide) => ({ ...slide, id: `${slide.id}-repeat` })));
  }
  return slides;
}

function extractEfemerideHighlights(text: string): string[] {
  const lower = text.toLowerCase();
  const highlights: string[] = [];
  if (/(nac|birth)/i.test(lower)) {
    highlights.push('Nacimiento');
  }
  if (/(fallec|murió|murio|muri|death|defunc)/i.test(lower)) {
    highlights.push('Fallecimiento');
  }
  if (/(fundaci|inaug)/i.test(lower)) {
    highlights.push('Fundación');
  }
  return highlights;
}

function buildLivestreamSlides(snapshot: RoomSnapshot): Slide[] {
  const streams = snapshot.dashboard.livestreams ?? [];
  if (!streams.length) return [];
  scheduleLivestreamValidation(streams);
  const healthyStreams = streams.filter((entry) => !shouldSkipLivestream(entry));
  if (!healthyStreams.length) {
    const labels = streams.map((stream) => stream.title ?? stream.id).filter(Boolean);
    const header = buildFocusHeader({ tag: 'Señales en vivo', title: 'Fuentes fuera de línea' });
    const body = `${header}<p class="muted">${labels.length ? escapeHtml(labels.join(', ')) : 'Sin señales activas'}</p>`;
    return [
      {
        id: 'livestream-offline',
        title: '',
        html: wrapFocusPanel(body, 'focus-panel--livestream focus-panel--offline')
      }
    ];
  }
  return healthyStreams.map((stream, index) => {
    const sourceLabel = stream.source ? stream.source : 'Fuente no especificada';
    const iframe = stream.embedUrl
      ? `<iframe class="livestream-frame" src="${escapeAttribute(stream.embedUrl)}" title="${escapeHtml(stream.title ?? 'Señal en vivo')}" allow="autoplay; encrypted-media" allowfullscreen loading="lazy"></iframe>`
      : '<div class="muted">Embed no disponible</div>';
    const header = buildFocusHeader({ tag: 'En vivo', title: stream.title ?? 'Señal en vivo', subtitle: sourceLabel });
    const body = `${header}<div class="focus-media">${iframe}</div>`;
    return {
      id: `livestream-${escapeHtml(stream.id ?? String(index))}`,
      title: '',
      html: wrapFocusPanel(body, 'focus-panel--livestream')
    };
  });
}

function buildPollSlide(snapshot: RoomSnapshot): Slide | null {
  const poll = snapshot.poll;
  if (!poll) return null;
  const totalVotes = poll.options.reduce((acc, option) => acc + option.votes, 0);
  const statusLabel = poll.status === 'open' ? 'Votación abierta' : 'Resultados finales';
  const updatedAt = formatChileanTime(snapshot.serverTime);
  const header = buildFocusHeader({ tag: statusLabel, title: poll.question, subtitle: `${totalVotes} voto${totalVotes === 1 ? '' : 's'} · ${updatedAt}` });
  const body = `${header}<div class="poll-options">${poll.options.map((option) => formatPollOption(option, totalVotes)).join('')}</div><footer class="focus-footer">Participa desde tu control en vivo.</footer>`;
  return {
    id: `poll-${escapeHtml(poll.id ?? 'activo')}`,
    title: '',
    html: wrapFocusPanel(body, 'focus-panel--poll')
  };
}

function buildPolymarketSlide(snapshot: RoomSnapshot): Slide | null {
  const markets = snapshot.dashboard.polymarket ?? [];
  if (!markets.length) return null;
  const entries = markets
    .map((market) => {
      const yesPercent = formatPercentValue(market.yesPrice, 1);
      const avatar = market.imageUrl
        ? `<img src="${escapeAttribute(market.imageUrl)}" alt="${escapeHtml(market.question)}">`
        : `<span class="avatar-fallback">${escapeHtml(market.question.charAt(0) ?? '?')}</span>`;
      const changeLabel = formatPolymarketChange(market.priceChanges?.oneDay);
      const changeClass = market.priceChanges?.oneDay
        ? market.priceChanges.oneDay > 0
          ? ' up'
          : market.priceChanges.oneDay < 0
            ? ' down'
            : ''
        : '';
      return `
        <li class="polymarket-card">
          <div class="polymarket-card__avatar">${avatar}</div>
          <div class="polymarket-card__body">
            <h4>${escapeHtml(market.question)}</h4>
            <div class="polymarket-card__meta">
              <span class="polymarket-card__yes">${yesPercent}</span>
              <span class="polymarket-card__change${changeClass}">${changeLabel}</span>
            </div>
          </div>
        </li>
      `;
    })
    .join('');
  const header = buildFocusHeader({ tag: 'Polymarket', title: 'Pronóstico presidencial', subtitle: `${markets.length} mercado${markets.length === 1 ? '' : 's'}` });
  const body = `${header}<ul class="polymarket-grid">${entries}</ul>`;
  return {
    id: 'polymarket',
    title: '',
    html: wrapFocusPanel(body, 'focus-panel--polymarket')
  };
}

function buildNewsSlides(snapshot: RoomSnapshot): Slide[] {
  const news = snapshot.dashboard.news ?? [];
  if (!news.length) return [];
  const limited = news.slice(0, NEWS_MAX_ITEMS);
  const pinnedCount = Math.min(NEWS_PINNED_COUNT, limited.length);
  const pinned = limited.slice(0, pinnedCount);
  const rotating = limited.slice(pinnedCount);
  const slides: Slide[] = [];
  if (pinned.length) {
    slides.push(createNewsSlide(pinned, 'news-latest', 'Últimas noticias'));
  }
  if (rotating.length) {
    const rotatingChunks = chunkArray(rotating, NEWS_VISIBLE_COUNT);
    const duplicates: Slide[] = [];
    rotatingChunks.forEach((chunk, index) => {
      const id = `news-rotating-${index}`;
      const slideTitle = index === 0 && !pinned.length ? 'Noticias en rotación' : 'Más titulares';
      const slide = createNewsSlide(chunk, id, slideTitle);
      slides.push(slide);
      const duplicateCount = Math.max(1, NEWS_ROTATION_DUPLICATION - 1);
      for (let dup = 0; dup < duplicateCount; dup++) {
        duplicates.push({ ...slide, id: `${id}-repeat-${dup + 1}` });
      }
    });
    if (duplicates.length) {
      slides.push(...duplicates);
    }
  }
  return slides;
}

function createNewsSlide(items: NewsItem[], id: string, title: string): Slide {
  const list = items
    .map((item) => {
      const titleText = escapeHtml(item.title ?? 'Titular');
      const href = item.link ? escapeAttribute(item.link) : '#';
      const targetAttrs = item.link ? ' target="_blank" rel="noopener"' : '';
      const timeLabel = formatNewsTime(item.publishedAt, item.isoDate);
      const brand = resolveNewsBrand(item);
      const brandLabel = brand ? escapeHtml(brand.label) : '';
      const badge = timeLabel ? `<span class="news-time">${escapeHtml(timeLabel)} CLT</span>` : '';
      const metaParts: string[] = [];
      if (badge) metaParts.push(badge);
      if (brandLabel) metaParts.push(brandLabel);
      const meta = metaParts.length ? `<span class="news-meta">${metaParts.join(' · ')}</span>` : '';
      return `
          <li>
            <a href="${href}"${targetAttrs}>
              <span class="news-title">${titleText}</span>
              ${meta}
            </a>
          </li>
        `;
    })
    .join('');
  const header = buildFocusHeader({ tag: 'Noticias', title, subtitle: 'Periodismo2.cl' });
  const body = `${header}<ol class="news-list">${list}</ol>`;
  return {
    id,
    title: '',
    html: wrapFocusPanel(body, 'focus-panel--news')
  };
}

function buildWeatherSlide(snapshot: RoomSnapshot): Slide | null {
  const state = lastWeatherState ?? deriveWeatherState(snapshot);
  if (!state) return null;
  const slotsHtml = state.slots.length
    ? state.slots
        .map((slot) => `
          <div class="forecast-slot" data-hour="${slot.targetHour}">
            <span class="icon">${slot.icon}</span>
            <span class="time">${slot.time}</span>
            <span class="temp">${slot.temp}</span>
            <span class="meta">${slot.meta}</span>
          </div>
        `)
        .join('')
    : '<p class="muted">Sin pronóstico horario disponible</p>';
  const summaryParts = [state.temperature];
  if (state.wind) summaryParts.push(`Viento ${state.wind}`);
  if (state.humidity) summaryParts.push(`Humedad ${state.humidity}`);
  const header = buildFocusHeader({ tag: state.descriptor.emoji, title: state.descriptor.label, subtitle: summaryParts.join(' · ') });
  const body = `${header}<div class="forecast-grid">${slotsHtml}</div>`;
  return {
    id: 'weather',
    title: '',
    html: wrapFocusPanel(body, 'focus-panel--weather')
  };
}

function buildRadioSlide(snapshot: RoomSnapshot): Slide | null {
  const state = lastRadioState ?? deriveRadioState(snapshot);
  if (!state) return null;
  const now = Date.now();
  const overridesHtml = state.overrides.length
    ? state.overrides.map((override) => formatOverride(override, now)).join('')
    : '<span class="muted">Sin ajustes temporales</span>';
  const tag = state.isPlaying ? 'Radio en vivo' : 'Radio en pausa';
  const subtitleParts = [`Volumen ${Math.round(state.volumePercent)}%`];
  if (state.ducking) subtitleParts.push('Atenuado por video');
  const header = buildFocusHeader({ tag, title: state.streamLabel, subtitle: subtitleParts.join(' · ') });
  const body = `${header}<p class="radio-track">${escapeHtml(state.nowPlaying)}</p><div class="overrides">${overridesHtml}</div>`;
  return {
    id: 'radio',
    title: '',
    html: wrapFocusPanel(body, 'focus-panel--radio')
  };
}

function buildQueueSlide(snapshot: RoomSnapshot): Slide | null {
  const queue = snapshot.youtubeQueue ?? [];
  if (!queue.length) return null;
  const list = queue.slice(0, 4)
    .map((item, index) => {
      const title = escapeHtml(item.title ?? 'Video');
      const duration = formatDuration(item.durationSec);
      const addedBy = item.addedBy ? escapeHtml(item.addedBy) : 'alguien';
      return `<li><span class="index">${index + 1}.</span> ${title}<span class="meta">${addedBy} • ${duration}</span></li>`;
    })
    .join('');
  const header = buildFocusHeader({ tag: 'Multimedia', title: 'Siguiente en pantalla', subtitle: `${queue.length} video${queue.length === 1 ? '' : 's'}` });
  const body = `${header}<ol class="queue-list">${list}</ol>`;
  return {
    id: 'queue',
    title: '',
    html: wrapFocusPanel(body, 'focus-panel--queue')
  };
}

function buildOverlaySlide(snapshot: RoomSnapshot): Slide | null {
  const overlays = snapshot.overlays ?? [];
  if (!overlays.length) return null;
  const now = Date.now();
  const list = overlays.slice(0, 3)
    .map((overlay) => {
      const remaining = Math.max(0, overlay.expiresAt - now);
      const remainingLabel = formatRemaining(remaining);
      const url = overlay.url ? `<a href="${escapeAttribute(overlay.url)}" target="_blank" rel="noopener">${escapeHtml(overlay.url)}</a>` : '';
      return `
        <li>
          <strong>${escapeHtml(overlay.title ?? overlay.kind ?? 'Overlay')}</strong>
          <div class="meta">${escapeHtml(overlay.addedBy ?? 'Equipo')} · ${remainingLabel || 'sin tiempo definido'}</div>
          <p>${escapeHtml(overlay.body ?? '')}</p>
          ${url}
        </li>
      `;
    })
    .join('');
  const header = buildFocusHeader({ tag: 'En pantalla', title: 'Overlays activos', subtitle: `${overlays.length} activo${overlays.length === 1 ? '' : 's'}` });
  const body = `${header}<ul class="overlay-list">${list}</ul>`;
  return {
    id: 'overlays',
    title: '',
    html: wrapFocusPanel(body, 'focus-panel--overlays')
  };
}

function buildNotesSlide(snapshot: RoomSnapshot): Slide | null {
  const notes = Array.isArray(snapshot.notes) ? snapshot.notes.slice(0, 4) : [];
  if (!notes.length) return null;
  const list = notes
    .map((note) => {
      const body = escapeHtml(note.message ?? '').replace(/\n+/g, '<br>');
      const author = escapeHtml(note.createdBy ?? 'Equipo');
      const timeLabel = formatChileanTime(note.createdAt ?? snapshot.serverTime);
      return `
        <li>
          <p>${body}</p>
          <span class="meta">${author} · ${escapeHtml(timeLabel)}</span>
        </li>
      `;
    })
    .join('');
  const header = buildFocusHeader({ tag: 'Avisos', title: 'Destacados' });
  const body = `${header}<ul class="notes-list">${list}</ul>`;
  return {
    id: 'notes',
    title: '',
    html: wrapFocusPanel(body, 'focus-panel--notes')
  };
}

function buildMetaSlide(snapshot: RoomSnapshot): Slide | null {
  const name = roomNameMap[snapshot.roomId] ?? snapshot.roomId;
  const timezone = snapshot.schedule.timezone ?? CHILE_TZ;
  const schedule = roomScheduleMap[snapshot.roomId] ?? snapshot.schedule.activeHours ?? FALLBACK_ACTIVE_HOURS;
  const tzLabel = timeZoneAbbr(timezone);
  const updatedAt = formatChileanTime(snapshot.serverTime);
  const header = buildFocusHeader({ tag: 'Pantalla', title: name, subtitle: `${schedule.start} – ${schedule.end} ${tzLabel}` });
  const body = `${header}<ul class="meta-list"><li><strong>Volumen base:</strong> ${snapshot.volume.base}% (min ${snapshot.volume.floor} / max ${snapshot.volume.ceiling})</li><li><strong>Actualizado:</strong> ${escapeHtml(updatedAt)}</li></ul>`;
  return {
    id: 'meta',
    title: '',
    html: wrapFocusPanel(body, 'focus-panel--meta')
  };
}

function chunkArray<T>(items: T[], size: number): T[][] {
  const result: T[][] = [];
  if (size <= 0) return result;
  for (let i = 0; i < items.length; i += size) {
    result.push(items.slice(i, i + size));
  }
  return result;
}

function scheduleLivestreamValidation(streams: LivestreamEntry[]): void {
  const now = Date.now();
  for (const entry of streams) {
    const record = livestreamHealth.get(entry.id);
    if (record && record.status === 'pending' && now - record.checkedAt < LIVESTREAM_VALIDATION_TIMEOUT_MS) {
      continue;
    }
    if (!record || now - record.checkedAt > LIVESTREAM_HEALTH_CACHE_MS) {
      validateLivestream(entry);
    }
  }
}

function shouldSkipLivestream(entry: LivestreamEntry): boolean {
  const record = livestreamHealth.get(entry.id);
  if (!record) return false;
  if (record.status === 'fail' && Date.now() - record.checkedAt < LIVESTREAM_HEALTH_CACHE_MS) {
    return true;
  }
  return false;
}

function validateLivestream(entry: LivestreamEntry): void {
  const thumb = resolveLivestreamThumbnail(entry);
  const now = Date.now();
  livestreamHealth.set(entry.id, { status: 'pending', checkedAt: now });
  if (!thumb) {
    if (isYoutubeEmbed(entry.embedUrl)) {
      validateYoutubeEmbed(entry);
    } else {
      markLivestreamStatus(entry.id, 'ok');
    }
    return;
  }
  const img = new Image();
  const src = thumb.includes('?') ? `${thumb}&ts=${now}` : `${thumb}?ts=${now}`;
  img.onload = () => markLivestreamStatus(entry.id, 'ok');
  img.onerror = () => {
    if (isYoutubeEmbed(entry.embedUrl)) {
      validateYoutubeEmbed(entry);
    } else {
      markLivestreamStatus(entry.id, 'fail');
    }
  };
  img.src = src;
}

function resolveLivestreamThumbnail(entry: LivestreamEntry): string | null {
  if (entry.thumbUrl) return entry.thumbUrl;
  if (!entry.embedUrl) return null;
  try {
    const url = new URL(entry.embedUrl);
    if (url.hostname.includes('youtube.com')) {
      const parts = url.pathname.split('/').filter(Boolean);
      const videoId = parts.length >= 2 && parts[0] === 'embed' ? parts[1] : null;
      if (videoId && videoId !== 'live_stream') {
        return `https://i.ytimg.com/vi/${videoId}/mqdefault.jpg`;
      }
    }
  } catch {
    return null;
  }
  return null;
}

function isYoutubeEmbed(embedUrl?: string): boolean {
  if (!embedUrl) return false;
  try {
    const host = new URL(embedUrl).hostname.toLowerCase();
    return host.includes('youtube.com') || host.includes('youtu.be');
  } catch {
    return false;
  }
}

function validateYoutubeEmbed(entry: LivestreamEntry): void {
  if (!entry.embedUrl) {
    markLivestreamStatus(entry.id, 'fail');
    return;
  }
  const infoUrl = buildYoutubeOembedUrl(entry.embedUrl);
  if (!infoUrl) {
    markLivestreamStatus(entry.id, 'fail');
    return;
  }
  const controller = new AbortController();
  let settled = false;
  const timeoutId = window.setTimeout(() => {
    if (settled) return;
    settled = true;
    controller.abort();
    markLivestreamStatus(entry.id, 'fail');
  }, LIVESTREAM_VALIDATION_TIMEOUT_MS);

  fetch(infoUrl, { signal: controller.signal })
    .then((response) => {
      if (settled) return;
      settled = true;
      markLivestreamStatus(entry.id, response.ok ? 'ok' : 'fail');
    })
    .catch(() => {
      if (settled) return;
      settled = true;
      markLivestreamStatus(entry.id, 'fail');
    })
    .finally(() => {
      window.clearTimeout(timeoutId);
    });
}

function buildYoutubeOembedUrl(embedUrl: string): string | null {
  try {
    const url = new URL('https://www.youtube.com/oembed');
    url.searchParams.set('url', embedUrl);
    url.searchParams.set('format', 'json');
    return url.toString();
  } catch {
    return null;
  }
}

function markLivestreamStatus(id: string, status: LivestreamHealthStatus): void {
  livestreamHealth.set(id, { status, checkedAt: Date.now() });
  if (!lastSnapshot) return;
  // Re-render to ensure health changes reflect on screen
  window.setTimeout(() => {
    if (lastSnapshot) {
      updateCentralSlides(lastSnapshot);
    }
  }, 0);
}

function formatPollOption(option: { id: string; label: string; votes: number }, totalVotes: number): string {
  const ratio = totalVotes > 0 ? option.votes / totalVotes : 0;
  const percentLabel = formatPercentValue(ratio, 1);
  const widthPercent = totalVotes > 0 ? Math.min(100, Math.max(4, Math.round(ratio * 100))) : 4;
  const votesLabel = option.votes === 1 ? '1 voto' : `${option.votes} votos`;
  return `
    <div class="poll-option">
      <div class="poll-option__label">${escapeHtml(option.label)}</div>
      <div class="poll-option__bar"><span style="width:${widthPercent}%"></span></div>
      <div class="poll-option__metrics"><strong>${percentLabel}</strong><span>${votesLabel}</span></div>
    </div>
  `;
}

function renderPolymarketSummary(market: PolymarketMarket): string {
  const yesPercent = formatPercentValue(market.yesPrice, 1);
  const noPercent = formatPercentValue(market.noPrice, 1);
  const lastTrade = formatPercentValue(market.lastTradePrice, 1);
  const volume24h = formatUsdCompact(market.volumeUsd24h);
  const volume1w = formatUsdCompact(market.volumeUsd1w);
  const liquidity = formatUsdCompact(market.liquidityUsd);
  const changeLabel = formatPolymarketChange(market.priceChanges?.oneDay);
  const changeClass = market.priceChanges?.oneDay
    ? market.priceChanges.oneDay > 0
      ? ' up'
      : market.priceChanges.oneDay < 0
      ? ' down'
      : ''
    : '';

  return `
    <div class="market-summary">
      <span><strong>Sí:</strong> ${yesPercent}</span>
      <span><strong>No:</strong> ${noPercent}</span>
      <span><strong>Último:</strong> ${lastTrade}</span>
      <span><strong>Vol 24h:</strong> ${volume24h}</span>
      <span><strong>Vol 7d:</strong> ${volume1w}</span>
      <span><strong>Liquidez:</strong> ${liquidity}</span>
      <span class="polymarket-change${changeClass}"><strong>Cambio 24h:</strong> ${changeLabel}</span>
    </div>
  `;
}

function renderPolymarketChart(market: PolymarketMarket): string {
  const yes = typeof market.yesPrice === 'number' ? Math.max(0, Math.min(1, market.yesPrice)) : null;
  const no = typeof market.noPrice === 'number' ? Math.max(0, Math.min(1, market.noPrice)) : null;
  const yesWidth = yes !== null ? Math.round(yes * 100) : null;
  const noWidth = no !== null ? Math.round(no * 100) : null;
  const yesLabel = formatPercentValue(market.yesPrice, 1);
  const noLabel = formatPercentValue(market.noPrice, 1);
  const changeLabel = formatPolymarketChange(market.priceChanges?.oneDay);
  const lastTrade = formatPercentValue(market.lastTradePrice, 1);
  const volume24h = formatUsdCompact(market.volumeUsd24h);
  const liquidity = formatUsdCompact(market.liquidityUsd);

  return `
    <div class="market-chart__row">
      <span>Sí</span>
      <div class="market-chart__bar">${yesWidth !== null ? `<span style="width:${yesWidth}%"></span>` : ''}</div>
      <strong>${yesLabel}</strong>
    </div>
    <div class="market-chart__row">
      <span>No</span>
      <div class="market-chart__bar">${noWidth !== null ? `<span style="width:${noWidth}%"></span>` : ''}</div>
      <strong>${noLabel}</strong>
    </div>
    <div class="market-chart__meta">
      <span>Cambio 24h ${changeLabel}</span>
      <span>Último ${lastTrade}</span>
      <span>Vol 24h ${volume24h}</span>
      <span>Liquidez ${liquidity}</span>
    </div>
  `;
}

function formatPolymarketChange(change?: number): string {
  if (typeof change !== 'number' || Number.isNaN(change)) return '—';
  if (change === 0) return '0,0%';
  return formatPercentValue(change, 1, { signed: true });
}

function shouldSkipIndicator(indicator: IndicatorSnapshot): boolean {
  const label = (indicator.label ?? '').toLowerCase();
  return INDICATOR_DENYLIST.some((entry) => label.includes(entry));
}

function normaliseIndicatorLabel(indicator: IndicatorSnapshot): string {
  const label = indicator.label ?? 'Indicador';
  if (/euro/i.test(label)) return 'Euro (€)';
  if (/d[oó]lar/i.test(label) && !/observado/i.test(label)) return 'Dólar observado';
  return label;
}

function formatIndicatorDisplay(indicator: IndicatorSnapshot): { value: string; unit?: string } {
  const numeric = parseNumericValue(indicator.value);
  if (numeric === null) {
    return { value: String(indicator.value ?? '—'), unit: indicator.unit };
  }
  if (shouldFormatCurrency(indicator)) {
    return { value: formatClpValue(numeric) };
  }
  if (shouldFormatAsPercent(indicator)) {
    let ratio = numeric;
    if (Math.abs(numeric) > 1) {
      ratio = numeric / 100;
    }
    return { value: formatPercentValue(ratio, 1) };
  }
  const formatted = new Intl.NumberFormat('es-CL', {
    maximumFractionDigits: Math.abs(numeric) >= 100 ? 0 : 2
  }).format(numeric);
  return { value: formatted, unit: indicator.unit };
}

function parseNumericValue(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  if (typeof value !== 'string') return null;
  const cleaned = value.trim();
  if (!cleaned) return null;
  let normalized = cleaned.replace(/[^0-9,.-]/g, '');
  const commaCount = (normalized.match(/,/g) ?? []).length;
  const dotCount = (normalized.match(/\./g) ?? []).length;
  if (commaCount > 0 && dotCount > 0) {
    if (normalized.lastIndexOf(',') > normalized.lastIndexOf('.')) {
      normalized = normalized.replace(/\./g, '').replace(',', '.');
    } else {
      normalized = normalized.replace(/,/g, '');
    }
  } else if (commaCount > 0) {
    normalized = normalized.replace(',', '.');
  }
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : null;
}

function shouldFormatCurrency(indicator: IndicatorSnapshot): boolean {
  const label = (indicator.label ?? '').toLowerCase();
  const unit = (indicator.unit ?? '').toLowerCase();
  return unit.includes('clp') || /d[oó]lar|euro|uf|utm|peso/.test(label);
}

function shouldFormatAsPercent(indicator: IndicatorSnapshot): boolean {
  const unit = (indicator.unit ?? '').toLowerCase();
  const label = (indicator.label ?? '').toLowerCase();
  return unit.includes('%') || unit.includes('porcentaje') || /ipc|inflaci[oó]n|variaci[oó]n/.test(label);
}

function formatClpValue(value: number): string {
  return new Intl.NumberFormat('es-CL', {
    style: 'currency',
    currency: 'CLP',
    maximumFractionDigits: 0
  }).format(value);
}

function renderTicker(snapshot: RoomSnapshot): void {
  if (!els.tickerTrack || !els.ticker) return;
  const items = buildTickerItems(snapshot);
  if (!items.length) {
    els.tickerTrack.innerHTML = '';
    els.ticker.classList.add('paused');
    return;
  }
  const html = items
    .map((item) => {
      const directionClass = item.direction && item.direction !== 'flat' ? ` ${item.direction}` : '';
      const change = item.change ? `<span class="ticker-change">${escapeHtml(item.change)}</span>` : '';
      return `<div class="ticker-item${directionClass}"><span>${escapeHtml(item.label)}</span><strong>${escapeHtml(item.value)}</strong>${change}</div>`;
    })
    .join('');
  els.tickerTrack.innerHTML = html + html;
  els.ticker.classList.remove('paused');
}

function buildTickerItems(snapshot: RoomSnapshot): TickerItem[] {
  const items: TickerItem[] = [];
  const newsItem = snapshot.dashboard.news?.[0];
  if (newsItem?.title) {
    const change = newsItem.source ? `${newsItem.source}` : formatNewsTime(newsItem.publishedAt, newsItem.isoDate);
    items.push({
      label: 'Titular',
      value: newsItem.title,
      change: change ?? undefined,
      direction: 'flat'
    });
  }

  const efemeride = (snapshot.dashboard.featured ?? []).find((entry) => entry.category === 'efemerides');
  if (efemeride) {
    const body = efemeride.description ?? efemeride.title;
    items.push({
      label: 'Efeméride',
      value: body ? body.replace(/\n+/g, ' ') : efemeride.title,
      change: efemeride.footer ?? undefined,
      direction: 'flat'
    });
  }

  const stockItems = buildStockTickerItems(snapshot.dashboard.indicators ?? []);
  items.push(...stockItems);

  const weatherItem = buildWeatherTickerItem(snapshot.dashboard.weather);
  if (weatherItem) {
    items.push(weatherItem);
  }

  const radioItem = buildRadioTickerItem(snapshot);
  if (radioItem) {
    items.push(radioItem);
  }

  return items;
}

function buildStockTickerItems(indicators: IndicatorSnapshot[]): TickerItem[] {
  const specs = [
    { ids: ['^ipsa', 'ipsa'], label: 'IPSA' },
    { ids: ['^gspc', 's&p 500', 'sandp'], label: 'S&P 500' },
    { ids: ['^dji', 'dow'], label: 'Dow Jones' },
    { ids: ['^ixic', 'nasdaq'], label: 'NASDAQ' },
    { ids: ['cobre', 'copper', 'cu'], label: 'Cobre' }
  ];
  const items: TickerItem[] = [];
  const lowerIndicators = indicators.filter((indicator) => !shouldSkipIndicator(indicator));
  for (const spec of specs) {
    const match = lowerIndicators.find((indicator) => {
      const id = (indicator.id ?? '').toLowerCase();
      const label = (indicator.label ?? '').toLowerCase();
      return spec.ids.some((candidate) => id === candidate || label.includes(candidate));
    });
    if (!match) continue;
    const formatted = formatIndicatorDisplay(match);
    const valueLabel = formatted.unit ? `${formatted.value} ${formatted.unit}` : formatted.value;
    items.push({
      label: spec.label,
      value: valueLabel,
      change: match.trend?.text ?? undefined,
      direction: match.trend?.direction ?? 'flat'
    });
  }
  return items;
}

function buildWeatherTickerItem(weather: RoomSnapshot['dashboard']['weather']): TickerItem | null {
  if (!weather) return null;
  const descriptor = describeWeather(weather.condition);
  const temp = typeof weather.temperatureC === 'number' ? `${weather.temperatureC.toFixed(1)}°C` : '—';
  const wind = typeof weather.windKmh === 'number' ? `${Math.round(weather.windKmh)} km/h` : null;
  const change = wind ? `Viento ${wind}` : descriptor.label;
  return {
    label: 'Clima',
    value: `${descriptor.emoji} ${temp}`,
    change,
    direction: 'flat'
  };
}

function buildRadioTickerItem(snapshot: RoomSnapshot): TickerItem | null {
  const stream = snapshot.radioStreams.find((s) => s.id === snapshot.radio.streamId) ?? snapshot.radioStreams[0];
  const streamLabel = stream ? stream.name : snapshot.radio.streamId;
  const nowPlaying = formatRadioNowPlaying(snapshot.radioNowPlaying, streamLabel);
  if (!nowPlaying) return null;
  return {
    label: 'Radio',
    value: nowPlaying,
    change: snapshot.radio.isPlaying ? 'En vivo' : 'Pausado',
    direction: snapshot.radio.isPlaying ? 'up' : 'flat'
  };
}


function renderParticipants(snapshot: RoomSnapshot): void {
  if (!els.participants) return;
  const participants = snapshot.participants;
  if (!participants.length) {
    els.participants.innerHTML = '<p class="muted">Nadie conectado</p>';
    if (previousParticipantIds.size) {
      previousParticipantIds = new Set();
      announceStatus('Pantalla libre de controles');
    }
    return;
  }
  els.participants.innerHTML = participants
    .map((p) => `
      <div class="participant">
        <div class="meta">
          <strong>${p.displayName}</strong>
          <span class="device">${formatDevice(p.deviceKind)}</span>
        </div>
        <span class="badge">${timeAgo(p.lastSeenAt)}</span>
      </div>
    `)
    .join('');

  const currentIds = new Set(participants.map((p) => p.id));
  const newlyAdded = participants.filter((p) => !previousParticipantIds.has(p.id));
  const removedCount = Array.from(previousParticipantIds).filter((id) => !currentIds.has(id)).length;
  previousParticipantIds = currentIds;
  if (newlyAdded.length) {
    const first = newlyAdded[0];
    announceStatus(newlyAdded.length === 1 ? `${first.displayName} se conectó` : `${newlyAdded.length} controles conectados`);
  } else if (removedCount > 0) {
    announceStatus(removedCount === 1 ? 'Un control se desconectó' : `${removedCount} controles se desconectaron`);
  }
}

function describeWeather(code: string | number | undefined): WeatherDescriptor {
  const weatherCode = Number(code);
  const mapping: Record<number, WeatherDescriptor> = {
    0: { label: 'Despejado', emoji: '☀️' },
    1: { label: 'Mayormente despejado', emoji: '🌤️' },
    2: { label: 'Parcialmente nublado', emoji: '⛅️' },
    3: { label: 'Nublado', emoji: '☁️' },
    45: { label: 'Niebla', emoji: '🌫️' },
    48: { label: 'Niebla helada', emoji: '🌫️' },
    51: { label: 'Llovizna débil', emoji: '🌦️' },
    53: { label: 'Llovizna', emoji: '🌦️' },
    55: { label: 'Llovizna intensa', emoji: '🌧️' },
    61: { label: 'Lluvia débil', emoji: '🌧️' },
    63: { label: 'Lluvia', emoji: '🌧️' },
    65: { label: 'Lluvia intensa', emoji: '⛈️' },
    71: { label: 'Nieve ligera', emoji: '🌨️' },
    73: { label: 'Nieve', emoji: '🌨️' },
    75: { label: 'Nieve intensa', emoji: '❄️' },
    80: { label: 'Chubascos leves', emoji: '🌦️' },
    81: { label: 'Chubascos', emoji: '🌧️' },
    82: { label: 'Chubascos fuertes', emoji: '⛈️' },
    95: { label: 'Tormenta', emoji: '⛈️' }
  };
  return Number.isFinite(weatherCode)
    ? mapping[weatherCode] ?? { label: 'Condición variable', emoji: '🌤️' }
    : { label: 'Condición variable', emoji: '🌤️' };
}

function announceStatus(message: string): void {
  if (!els.statusMessage) return;
  els.statusMessage.textContent = message;
  if (statusMessageTimer) {
    window.clearTimeout(statusMessageTimer);
  }
  statusMessageTimer = window.setTimeout(() => {
    if (els.statusMessage) els.statusMessage.textContent = '';
  }, 6000);
}

function updateMeta(snapshot: RoomSnapshot): void {
  const name = roomNameMap[snapshot.roomId] ?? snapshot.roomId;
  const timezone = snapshot.schedule.timezone ?? CHILE_TZ;
  const scheduleOverride = roomScheduleMap[snapshot.roomId];
  const activeSnapshot = snapshot.schedule.activeHours ?? FALLBACK_ACTIVE_HOURS;
  roomScheduleMap[snapshot.roomId] = scheduleOverride ?? activeSnapshot;
  timeSignal?.setTimezone(timezone);
  if (els.activeHours) {
    const schedule = roomScheduleMap[snapshot.roomId] ?? FALLBACK_ACTIVE_HOURS;
    const tzLabel = timeZoneAbbr(timezone);
    els.activeHours.textContent = `${escapeHtml(name)} • ${schedule.start} – ${schedule.end} ${escapeHtml(tzLabel)}`;
  }
}

function renderOtp(payload: OtpPayload): void {
  if (els.otpCode) {
    els.otpCode.textContent = payload.code;
    els.otpCode.classList.remove('flash');
    void els.otpCode.offsetWidth;
    els.otpCode.classList.add('flash');
  }
  persistOtp(payload);
  refreshQrCanvases();
  if (!otpManualVisible) {
    showOtpOverlay('auto');
  }
  scheduleOtpExpiryRefresh(payload);
  announceStatus('Código OTP actualizado');
  updateOtpExpiry();
  if (otpTimer) window.clearInterval(otpTimer);
  otpTimer = window.setInterval(updateOtpExpiry, 1000);
}

function startVersionWatcher(): void {
  checkVersion();
  setInterval(checkVersion, VERSION_POLL_MS);
}

async function checkVersion(): Promise<void> {
  try {
    const res = await fetch(`/version.json?ts=${Date.now()}`, { cache: 'no-store' });
    if (!res.ok) return;
    const data = await res.json();
    const incoming = data?.buildId ?? null;
    if (!currentBuildId) {
      currentBuildId = incoming;
      return;
    }
    if (incoming && incoming !== currentBuildId) {
      console.info('[kiosk] nueva versión detectada, recargando');
      window.location.reload();
    }
  } catch (err) {
    console.warn('[kiosk] versión no disponible', err);
  }
}

function updateOtpExpiry(): void {
  if (!els.otpExpiry || !lastOtp) return;
  const remaining = Math.max(0, lastOtp.expiresAt - Date.now());
  const seconds = Math.ceil(remaining / 1000);
  els.otpExpiry.textContent = `Código caduca en ${seconds}s`;
}

function startClock(): void {
  updateClock();
  setInterval(updateClock, 1000);
}

function updateClock(): void {
  const now = Date.now();
  if (els.clock) {
    els.clock.textContent = formatChileanTime(now);
  }
  if (els.clockDate) {
    const label = new Intl.DateTimeFormat('es-CL', {
      weekday: 'long',
      day: 'numeric',
      month: 'long'
    }).format(new Date(now));
    els.clockDate.textContent = label.charAt(0).toUpperCase() + label.slice(1);
  }
}

function refreshQrCanvases(includeOverlay = false): void {
  if (!controlUrl) {
    if (els.qrNavbarBlock) {
      els.qrNavbarBlock.classList.add('is-disabled');
    }
    if (includeOverlay) {
      renderQr('', els.qrCanvas);
    }
    return;
  }
  renderQr(controlUrl, els.qrCanvas);
  renderQr(controlUrl, els.qrNavbarCanvas, 180);
  if (els.qrNavbarBlock) {
    els.qrNavbarBlock.classList.remove('is-disabled');
  }
}

function renderQr(target: string, canvas: HTMLCanvasElement | null, width = 220): void {
  if (!canvas) return;
  QRCode.toCanvas(canvas, target, { margin: 1, width }, (err) => {
    if (err) console.error('QR error', err);
  });
}

function formatDuration(durationSec?: number): string {
  if (!durationSec) return '–';
  const minutes = Math.floor(durationSec / 60);
  const seconds = durationSec % 60;
  return `${minutes}:${String(seconds).padStart(2, '0')}`;
}

function formatRemaining(milliseconds: number): string {
  const totalSeconds = Math.max(0, Math.round(milliseconds / 1000));
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  if (hours > 0) {
    return minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`;
  }
  if (minutes > 0) {
    return seconds > 0 ? `${minutes}m ${seconds}s` : `${minutes}m`;
  }
  return seconds > 0 ? `${seconds}s` : '';
}

function selectForecastIcon(point: { rainChance?: number }, fallbackCondition?: string): string {
  const rain = typeof point?.rainChance === 'number' ? point.rainChance : null;
  if (rain !== null) {
    if (rain >= 70) return '🌧️';
    if (rain >= 40) return '🌦️';
  }
  if (fallbackCondition !== undefined) {
    const descriptor = describeWeather(fallbackCondition);
    if (descriptor.emoji) return descriptor.emoji;
  }
  if (rain !== null && rain >= 15) return '⛅️';
  return '☀️';
}

function formatPercentValue(value?: number, fractionDigits = 1, options: { signed?: boolean } = {}): string {
  if (typeof value !== 'number' || Number.isNaN(value)) return '—';
  const formatter = new Intl.NumberFormat('es-CL', {
    style: 'percent',
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits
  });
  const formatted = formatter.format(value);
  if (options.signed && value > 0) {
    return `+${formatted}`;
  }
  return formatted;
}

function formatUsd(value?: number, fractionDigits = 0): string {
  if (typeof value !== 'number' || Number.isNaN(value)) return '—';
  const formatter = new Intl.NumberFormat('es-CL', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits
  });
  return formatter.format(value);
}

function formatUsdCompact(value?: number): string {
  if (typeof value !== 'number' || Number.isNaN(value)) return '—';
  const formatter = new Intl.NumberFormat('es-CL', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
    notation: 'compact'
  });
  return formatter.format(value);
}

function formatRadioNowPlaying(nowPlaying: RadioNowPlaying | undefined, fallback: string): string {
  if (!nowPlaying || !nowPlaying.title) {
    return fallback;
  }
  const title = nowPlaying.title.trim();
  const artist = nowPlaying.artist?.trim();
  return artist ? `${title} — ${artist}` : title;
}

function formatDevice(device?: string): string {
  switch (device) {
    case 'phone': return '📱 Celular';
    case 'tablet': return '📲 Tablet';
    case 'laptop': return '💻 Notebook';
    case 'desktop': return '🖥️ Escritorio';
    case 'watch': return '⌚ Watch';
    default: return 'Dispositivo';
  }
}

function timeAgo(timestamp: number): string {
  const delta = Math.max(0, Date.now() - timestamp);
  const minutes = Math.floor(delta / 60000);
  if (minutes < 1) return 'ahora';
  if (minutes === 1) return 'hace 1 min';
  if (minutes < 60) return `hace ${minutes} min`;
  const hours = Math.floor(minutes / 60);
  if (hours === 1) return 'hace 1 h';
  return `hace ${hours} h`;
}

function escapeHtml(value: unknown): string {
  if (value === null || value === undefined) return '';
  return String(value).replace(/[&<>"']/g, (char) => {
    switch (char) {
      case '&':
        return '&amp;';
      case '<':
        return '&lt;';
      case '>':
        return '&gt;';
      case '"':
        return '&quot;';
      case "'":
        return '&#39;';
      default:
        return char;
    }
  });
}

function escapeAttribute(value: string): string {
  try {
    const url = new URL(value, window.location.href);
    if (!/^https?:$/i.test(url.protocol)) {
      return '#';
    }
    return escapeHtml(url.toString());
  } catch {
    return '#';
  }
}

function safeCssUrl(value: string): string {
  try {
    const url = new URL(value, window.location.href);
    if (!/^https?:$/i.test(url.protocol)) {
      return '';
    }
    return url.toString().replace(/["'\\)]/g, '');
  } catch {
    return '';
  }
}

function formatNewsTime(publishedAt?: number, isoDate?: string): string {
  const timestamp = typeof publishedAt === 'number' && Number.isFinite(publishedAt)
    ? publishedAt
    : isoDate
      ? Date.parse(isoDate)
      : NaN;
  if (!Number.isFinite(timestamp)) return '';
  return new Intl.DateTimeFormat('es-CL', {
    hour: '2-digit',
    minute: '2-digit',
    timeZone: CHILE_TZ
  }).format(new Date(timestamp));
}

function resolveNewsBrand(item: { link?: string; source?: string }): { label: string; logo?: string } | null {
  const host = extractHostname(item.link);
  if (host && NEWS_BRANDS[host]) {
    return { ...NEWS_BRANDS[host] };
  }
  const normalizedSource = item.source ? normalizeLabel(item.source) : '';
  if (normalizedSource) {
    for (const brand of Object.values(NEWS_BRANDS)) {
      if (normalizeLabel(brand.label) === normalizedSource) {
        return { ...brand };
      }
    }
  }
  if (item.source) {
    return { label: item.source };
  }
  if (host) {
    return { label: host };
  }
  return null;
}

function extractHostname(value?: string): string | null {
  if (!value) return null;
  try {
    const url = new URL(value, window.location.href);
    return url.hostname.replace(/^www\./i, '').toLowerCase();
  } catch {
    return null;
  }
}

function normalizeLabel(value: string): string {
  return value
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase()
    .trim();
}

function formatChileanTime(epochMs: number): string {
  return new Intl.DateTimeFormat('es-CL', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
    timeZone: CHILE_TZ
  }).format(new Date(epochMs));
}

function formatChileanDate(epochMs: number): string {
  const date = new Date(epochMs);
  const formatted = new Intl.DateTimeFormat('es-CL', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    timeZone: CHILE_TZ
  }).format(date);
  return formatted.charAt(0).toUpperCase() + formatted.slice(1);
}

function timeZoneAbbr(timeZone: string): string {
  try {
    const parts = new Intl.DateTimeFormat('es-CL', {
      timeZone,
      timeZoneName: 'short'
    }).formatToParts(new Date());
    const zone = parts.find((part) => part.type === 'timeZoneName')?.value;
    return zone ?? timeZone;
  } catch {
    return timeZone;
  }
}

export {
  deriveWeatherState,
  deriveRadioState,
  buildCentralSlides,
  buildSideSlides,
  renderPolymarketSummary,
  renderPolymarketChart,
  formatPollOption,
  chunkArray,
  computeOtpRefreshDelay
};
